import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useEffect, useRef, useState, useMemo } from 'react';
import { supabase } from '../lib/supabaseClient';
import toast from 'react-hot-toast';
import { formatearNumeroCL } from '../utils/formatUtils';
import { rendimientoSegunColor } from '../utils/rendimientoUtils';
import GuiaModal from '../components/GuiaModal';
import CompletarEntregaModal from '../components/CompletarEntregaModal';
import AyudaModal from '../components/AyudaModal';
import TablaScroll from '../components/TablaScroll';

// Cache management utility
const clearPageCache = () => {
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => {
        caches.delete(cacheName);
      });
    });
  }
  // No forzar reload automático - solo limpiar caché
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function Dashboard() {
  const user = useAuthStore((state) => state.user);
  const puede = useAuthStore((state) => state.puede);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();

  const [tonerStats, setTonerStats] = useState({});
  const [porClienteStats, setPorClienteStats] = useState({});
  const [rendimientoStats, setRendimientoStats] = useState({});
  const [pendientes, setPendientes] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [personalMap, setPersonalMap] = useState({});
  const [observacionModal, setObservacionModal] = useState(null);
  // Modal de guía: recibe todos los tóners del mismo cambio (una sola guía)
  const [guiaModal, setGuiaModal] = useState({ isOpen: false, registros: [] });
  // Cambios aprobados a los que les falta la persona que entrega o la fecha
  // de despacho. Se pueden completar en cualquier momento, con o sin guía.
  const [porCompletar, setPorCompletar] = useState([]);
  const [completarModal, setCompletarModal] = useState({ isOpen: false, registros: [] });
  const recargarPorCompletarRef = useRef(null);
  const [filtroMeses, setFiltroMeses] = useState(1); // Por defecto mes actual
  
  // Modal de aprobación con selección de tipo de tóner.
  // Se aprueban juntos todos los tóners del mismo cambio (mismo grupo).
  const [modalAprobacion, setModalAprobacion] = useState({ isOpen: false, registros: [] });
  // Tipo de tóner elegido para cada tóner del grupo: { idRegistro: idTipo }
  const [tiposPorRegistro, setTiposPorRegistro] = useState({});
  const [tiposToner, setTiposToner] = useState([]);
  const [personalSeleccionado, setPersonalSeleccionado] = useState('');
  const [personalEmpresa, setPersonalEmpresa] = useState([]);
  const [fechaEntrega, setFechaEntrega] = useState('');
  // New state for observaciones input in approval modal
  const [observacionesAprobacion, setObservacionesAprobacion] = useState('');

  // Estado para notificaciones de observaciones editadas por admin
  const [notificacionesObservaciones, setNotificacionesObservaciones] = useState([]);

  // Mapa de fechas de despacho programadas (solo visual): registroId -> fecha
  // Persistido en localStorage para mantener entre páginas
  const [fechasDespacho, setFechasDespacho] = useState(() => {
    try {
      const saved = localStorage.getItem('fechasDespacho');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Mapa visual: serie -> "Tóner esperado" (nombre en tipos_toner)
  const [tonerEsperadoPorSerie, setTonerEsperadoPorSerie] = useState({});

  // Mapa visual: serie -> departamento (columna "departamento" de maquinas).
  // El departamento es un dato de la máquina, no del registro, así que se
  // lee de la tabla maquinas y se muestra junto a cada registro.
  const [departamentoPorSerie, setDepartamentoPorSerie] = useState({});

  // Mapa de guías: clave (serie_fecha_cliente_color) -> guia
  const [guiasRegistros, setGuiasRegistros] = useState({});
  // Registros ya aprobados que todavía no tienen guía de despacho.
  // Se muestran a TODAS las secretarias (sin filtrar por quién los creó),
  // para que ninguno quede sin completar.
  const [pendientesGuia, setPendientesGuia] = useState([]);

  // Funciones para gestionar notificaciones de observaciones editadas
  const guardarNotificacionObservacion = (registroInfo, observacionOriginal, observacionEditada) => {
    try {
      const notificaciones = JSON.parse(localStorage.getItem('notificaciones_observaciones') || '[]');
      const nuevaNotificacion = {
        id: `${registroInfo.serie_maquina}_${registroInfo.fecha}_${Date.now()}`,
        serie_maquina: registroInfo.serie_maquina,
        cliente_nombre: registroInfo.clientes?.nombre || registroInfo.cliente_nombre,
        fecha: registroInfo.fecha,
        observacion_original: observacionOriginal,
        observacion_editada: observacionEditada,
        timestamp: new Date().toISOString(),
        leida: false
      };
      notificaciones.push(nuevaNotificacion);
      localStorage.setItem('notificaciones_observaciones', JSON.stringify(notificaciones));
    } catch (error) {
      console.error('Error al guardar notificación:', error);
    }
  };

  const obtenerNotificacionesPendientes = () => {
    try {
      const notificaciones = JSON.parse(localStorage.getItem('notificaciones_observaciones') || '[]');
      return notificaciones.filter(n => !n.leida);
    } catch (error) {
      console.error('Error al obtener notificaciones:', error);
      return [];
    }
  };

  const marcarNotificacionComoLeida = (notificacionId) => {
    try {
      const notificaciones = JSON.parse(localStorage.getItem('notificaciones_observaciones') || '[]');
      const actualizadas = notificaciones.map(n =>
        n.id === notificacionId ? { ...n, leida: true } : n
      );
      localStorage.setItem('notificaciones_observaciones', JSON.stringify(actualizadas));
      setNotificacionesObservaciones(actualizadas.filter(n => !n.leida));
    } catch (error) {
      console.error('Error al marcar notificación:', error);
    }
  };

  const marcarTodasNotificacionesLeidas = () => {
    try {
      const notificaciones = JSON.parse(localStorage.getItem('notificaciones_observaciones') || '[]');
      const actualizadas = notificaciones.map(n => ({ ...n, leida: true }));
      localStorage.setItem('notificaciones_observaciones', JSON.stringify(actualizadas));
      setNotificacionesObservaciones([]);
    } catch (error) {
      console.error('Error al marcar todas las notificaciones:', error);
    }
  };

  // Verificar si un registro tiene notificación de observación editada pendiente
  const tieneNotificacionObservacion = (registro) => {
    return notificacionesObservaciones.some(n =>
      n.serie_maquina === registro.serie_maquina &&
      n.fecha?.split('T')[0] === registro.fecha?.split('T')[0]
    );
  };

  // Obtener la notificación de un registro específico
  const obtenerNotificacionRegistro = (registro) => {
    return notificacionesObservaciones.find(n =>
      n.serie_maquina === registro.serie_maquina &&
      n.fecha?.split('T')[0] === registro.fecha?.split('T')[0]
    );
  };

  // Marcar notificación como leída al ver observaciones
  const verObservacionConNotificacion = (registro) => {
    const notificacion = obtenerNotificacionRegistro(registro);
    if (notificacion) {
      marcarNotificacionComoLeida(notificacion.id);
    }
    setObservacionModal(registro.observaciones);
  };

  // Guardar fechas en localStorage cada vez que cambien
  useEffect(() => {
    try {
      localStorage.setItem('fechasDespacho', JSON.stringify(fechasDespacho));
    } catch (error) {
      console.error('Error al guardar fechas en localStorage:', error);
    }
  }, [fechasDespacho]);

  // Cargar notificaciones de observaciones editadas para secretaria (sin mostrar toast)
  useEffect(() => {
    if (user?.role === 'secretaria') {
      const notificacionesPendientes = obtenerNotificacionesPendientes();
      setNotificacionesObservaciones(notificacionesPendientes);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatearFecha = (fechaStr) => {
    const fecha = new Date(fechaStr);
    const opcionesFecha = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return `${fecha.toLocaleDateString([], opcionesFecha)}`;
  };

  // Formatear fecha sin problema de zona horaria (para fechas programadas)
  const formatearFechaSinZonaHoraria = (fechaStr) => {
    if (!fechaStr) return '';
    const [year, month, day] = fechaStr.split('-');
    return `${day}-${month}-${year}`;
  };

  // Obtener fecha programada para un registro usando múltiples claves
  const obtenerFechaProgramada = (registro) => {
    if (!registro) return null;

    // Primero la que está guardada en la base: la ve cualquiera, desde
    // cualquier computador. Lo del navegador queda solo como respaldo para
    // los registros antiguos, de antes de que existiera la columna.
    if (registro.fecha_despacho) return registro.fecha_despacho;

    // Normalizar la fecha (puede venir con o sin hora)
    const fechaNormalizada = registro.fecha ? registro.fecha.split('T')[0] : '';

    // Intentar obtener la fecha usando diferentes claves
    const claveSerieFecha = `${registro.serie_maquina}_${fechaNormalizada}`;
    const claveRegistroId = `reg_${registro.id}`;

    return fechasDespacho[claveRegistroId] || fechasDespacho[claveSerieFecha] || null;
  };

  const getEmailById = (id) => {
    const user = usuarios.find((u) => u.id === id);
    return user?.email || '—';
  };

  // Los tóners cambiados en la misma visita (mismo grupo_id) se muestran,
  // aprueban y despachan juntos. Los registros antiguos, sin grupo, quedan
  // cada uno por su cuenta, igual que siempre.
  const gruposPendientes = useMemo(() => agruparRegistros(pendientes), [pendientes]);
  const gruposGuia = useMemo(() => agruparRegistros(pendientesGuia), [pendientesGuia]);
  const gruposPorCompletar = useMemo(() => agruparRegistros(porCompletar), [porCompletar]);

  // Qué le falta a un cambio, para decirlo en pantalla
  const queFalta = (grupo) => {
    const falta = [];
    if (!grupo.registros.some((r) => r.entregado_por)) falta.push('la persona que entrega');
    if (!grupo.registros.some((r) => r.fecha_despacho)) falta.push('la fecha de despacho');
    return falta.join(' y ');
  };

  // Fecha de despacho programada de un cambio (si el admin ya la puso)
  const obtenerFechaProgramadaGrupo = (grupo) => {
    const fecha = grupo.registros
      .map((r) => obtenerFechaProgramada(r) || fechasDespacho[r.id])
      .find(Boolean);
    return fecha ? formatearFechaSinZonaHoraria(fecha) : '';
  };

  // Tóners que se están aprobando en el modal (uno o todo el cambio)
  const registrosAprobacion = modalAprobacion.registros || [];
  const primerAprobacion = registrosAprobacion[0] || null;
  const faltaTipoEnAlgunColor = registrosAprobacion.some((r) => !tiposPorRegistro[r.id]);

  //Auto-actualización cada 30 segundos
  useEffect(() => {
    // Clear cache on page entry
    clearPageCache();
    
    // (Se quitó la consulta a users_livianos: el saludo usa el correo de la
    // sesión y el historial que mostraba correos está oculto. En algunas bases esa
    // vista no siempre se puede leer y llenaba la consola de errores.)

    const cargarEstadisticas = async () => {
      const { data, error } = await supabase
        .from('registros')
        .select(`fecha, cliente_id, modelo_toner_id, contador_actual, ultimo_contador, color, serie_maquina, clientes(nombre), toners(modelo, rendimiento), tipos_toner(nombre), aprobado_por`);

      if (error) return toast.error('Error al cargar estadísticas');

      // Calcular fecha límite para el gráfico de cambios mensuales
      const fechaLimite = new Date();
      fechaLimite.setMonth(fechaLimite.getMonth() - filtroMeses);

      const tonerMensual = {};
      const porCliente = {};
      const rendimiento = {};

      data.forEach((reg) => {
        const fecha = new Date(reg.fecha);
        const año = fecha.getFullYear();
        const mes = fecha.getMonth();
        const claveAñoMes = `${año}-${mes}`;
        const modelo = reg.toners?.modelo || 'Desconocido';
        const tipoToner = reg.tipos_toner?.nombre || 'Sin tipo';
        const modeloConTipo = `${modelo} (${tipoToner})`;
        const cliente = reg.clientes?.nombre || 'Sin cliente';

        if (fecha >= fechaLimite) {
          if (!tonerMensual[claveAñoMes]) tonerMensual[claveAñoMes] = {};
          tonerMensual[claveAñoMes][modeloConTipo] = (tonerMensual[claveAñoMes][modeloConTipo] || 0) + 1;

          if (!porCliente[claveAñoMes]) porCliente[claveAñoMes] = {};
          porCliente[claveAñoMes][cliente] = (porCliente[claveAñoMes][cliente] || 0) + 1;
        }

        // Rendimiento: se agrupa por modelo+tipo y, dentro de eso, por COLOR.
        // Un tóner de color (Cyan, Magenta, Yellow) no rinde igual que el negro,
        // por eso se contabilizan por separado.
        const modeloConTipoRendimiento = `${modelo} (${tipoToner})`;
        const colorReg = reg.color || 'Black';

        if (!rendimiento[modeloConTipoRendimiento]) {
          rendimiento[modeloConTipoRendimiento] = {};
        }
        if (!rendimiento[modeloConTipoRendimiento][colorReg]) {
          rendimiento[modeloConTipoRendimiento][colorReg] = { total: 0, cambios: 0 };
        }

        const diferencia = (reg.contador_actual || 0) - (reg.ultimo_contador || 0);
        if (diferencia > 0 && (reg.ultimo_contador || 0) > 0) {
          rendimiento[modeloConTipoRendimiento][colorReg].total += diferencia;
          // IMPORTANTE: se cuenta cada CAMBIO de tóner, no cada máquina.
          // Antes se dividía por la cantidad de máquinas, lo que hacía que el
          // promedio creciera indefinidamente (ej: 141.616 páginas en un GPR-57).
          rendimiento[modeloConTipoRendimiento][colorReg].cambios += 1;
        }
      });

      // Promedio real = total de páginas / cantidad de cambios registrados
      const promedio = {};
      for (const modelo in rendimiento) {
        for (const color in rendimiento[modelo]) {
          const { total, cambios } = rendimiento[modelo][color];
          if (cambios > 0) {
            if (!promedio[modelo]) promedio[modelo] = {};
            promedio[modelo][color] = Math.round(total / cambios);
          }
        }
      }

      setTonerStats(tonerMensual);
      setPorClienteStats(porCliente);
      setRendimientoStats(promedio);
    };

    // Departamento de cada máquina, para mostrarlo junto a los registros.
    // Se pide una sola vez al entrar: solo trae serie y departamento, así que
    // pesa muy poco aunque haya muchas máquinas.
    const cargarDepartamentos = async () => {
      const { data, error } = await supabase
        .from('maquinas')
        .select('serie, departamento');

      if (error) return;

      const mapa = {};
      (data || []).forEach((m) => {
        if (m.serie) mapa[m.serie] = m.departamento || '';
      });
      setDepartamentoPorSerie(mapa);
    };

    const cargarPendientes = async () => {
      const { data, error } = await supabase
        .from('registros_pendientes')
        .select(`*, clientes(nombre), toners(modelo, rendimiento, rendimiento_color), tipos_toner(nombre)`)
        .order('creado_el', { ascending: false });

      if (!error) {
        setPendientes(data || []);
        // —— Visual: obtener "Tóner esperado" desde máquinas (sin tocar lógica de aprobación)
        const series = [...new Set((data || []).map(r => r.serie_maquina).filter(Boolean))];
        if (series.length) {
          // 1) Obtener tipo_toner_id por serie
          const { data: maquinas } = await supabase
            .from('maquinas')
            .select('serie, tipo_toner_id')
            .in('serie', series);

          const idsTipo = [...new Set((maquinas || []).map(m => m.tipo_toner_id).filter(Boolean))];

          // 2) Mapear id -> nombre
          let mapaTipos = {};
          if (idsTipo.length) {
            const { data: tipos } = await supabase
              .from('tipos_toner')
              .select('id, nombre')
              .in('id', idsTipo);
            mapaTipos = Object.fromEntries((tipos || []).map(t => [t.id, t.nombre]));
          }

          // 3) serie -> nombre tipo esperado
          const mapaSerie = {};
          (maquinas || []).forEach(m => {
            mapaSerie[m.serie] = mapaTipos[m.tipo_toner_id] || '—';
          });
          setTonerEsperadoPorSerie(mapaSerie);
        } else {
          setTonerEsperadoPorSerie({});
        }
      }
    };

      // Carga los registros YA APROBADOS que aún no tienen guía de despacho.
      // A diferencia del historial personal, aquí NO se filtra por creador:
      // así, si un registro lo creó otra persona, igual aparece y se le puede
      // asignar la guía. Antes esto provocaba que registros quedaran "perdidos".
      const cargarPendientesGuia = async () => {
        // `grupo_id` junta los tóners del mismo cambio. Si la base todavía
        // no tiene esa columna (falta el script 16), se vuelve a pedir sin
        // ella: la pantalla funciona igual, con un registro por tóner.
        const consultar = (conGrupo) =>
          supabase
            .from('registros')
            .select(`id,
                    fecha,
                    guia,
                    ${conGrupo ? 'grupo_id,' : ''}
                    cliente_id,
                    modelo_maquina,
                    serie_maquina,
                    color,
                    contador_actual,
                    ultimo_contador,
                    observaciones,
                    entregado_por,
                    clientes(nombre),
                    toners(modelo),
                    tipos_toner_instalado:tipo_toner_instalado_id(nombre)`)
            .is('guia', null)
            .order('fecha', { ascending: false });

        let { data, error } = await consultar(true);

        if (error && String(error.message || '').includes('grupo_id')) {
          console.warn('La base aún no tiene la columna grupo_id (script 16).');
          ({ data, error } = await consultar(false));
        }

        if (!error) {
          setPendientesGuia(data || []);
        } else {
          console.error('Error al cargar pendientes de guía:', error);
        }
      };

      // Lo que quedó sin persona de entrega o sin fecha de despacho.
      // Si la base todavía no tiene la columna (falta el script 20), la
      // sección simplemente no aparece y el resto sigue funcionando igual.
      const cargarPorCompletar = async () => {
        const { data, error } = await supabase
          .from('registros')
          .select(`id, fecha, guia, grupo_id, cliente_id, modelo_maquina, serie_maquina,
                   color, contador_actual, entregado_por, fecha_despacho,
                   clientes(nombre), toners(modelo)`)
          .or('entregado_por.is.null,fecha_despacho.is.null')
          .order('fecha', { ascending: false })
          .limit(100);

        if (error) {
          if (!String(error.message || '').includes('fecha_despacho')) {
            console.error('Error al cargar los datos por completar:', error);
          }
          setPorCompletar([]);
          return;
        }
        setPorCompletar(data || []);
      };

      const cargarHistorial = async () => {
        // Solo lectura para mostrar columnas nuevas (visual)
        const { data, error } = await supabase
          .from('registros_historial')
          .select(`id,
                  fecha,
                  cliente_id,
                  modelo_toner_id,
                  tipo_toner_id,
                  resultado,
                  observaciones,
                  aprobado_por,
                  entregado_por,
                  serie_maquina,
                  contador_actual,
                  ultimo_contador,
                  toners(modelo, rendimiento, rendimiento_color),
                  tipos_toner(nombre),
                  clientes(nombre),
                  color`)
          // Antes se filtraba por .eq('creado_por', user.id), lo que hacía que la
          // secretaria solo viera los registros creados por ella misma. Si un
          // registro lo creaba otra persona (u otro usuario), desaparecía de su
          // historial. Ahora ve todos, igual que en "Pendientes de Guía".
          .order('fecha', { ascending: false })
          .limit(100);

        if (!error) {
          setHistorial(data || []);

          // Cargar las guías desde la tabla registros para los registros aprobados
          const registrosAprobados = (data || []).filter(r => r.resultado === 'aprobado');
          if (registrosAprobados.length > 0) {
            const { data: registrosConGuia, error: guiaError } = await supabase
              .from('registros')
              .select('serie_maquina, fecha, cliente_id, color, guia')
              .not('guia', 'is', null);

            if (!guiaError && registrosConGuia) {
              const mapaGuias = {};
              registrosConGuia.forEach(r => {
                // Crear clave única usando serie_fecha_cliente_color
                const fechaNormalizada = r.fecha ? r.fecha.split('T')[0] : r.fecha;
                const clave = `${r.serie_maquina}_${fechaNormalizada}_${r.cliente_id}_${r.color}`;
                mapaGuias[clave] = r.guia;
              });
              setGuiasRegistros(mapaGuias);
            }
          }
        }
      };

    const cargarTiposToner = async () => {
      const { data, error } = await supabase.from('tipos_toner').select('*');
      if (!error) setTiposToner(data || []);
    };

    const cargarPersonalEmpresa = async () => {
      const { data, error } = await supabase.from('personal_empresa').select('*').order('nombre');
      if (!error) {
        setPersonalEmpresa(data || []);
        // Crear mapa para búsqueda rápida
        const mapa = {};
        (data || []).forEach(persona => {
          mapa[persona.id] = persona.nombre;
        });
        setPersonalMap(mapa);
      }
    };

    const cargarDatos = () => {
      if (user) {
        cargarTiposToner();
        cargarPersonalEmpresa();
        if (puede('aprobar_registros')) {
          cargarEstadisticas();
        }
        // Admin y secretarias ven los registros pendientes (se piden una sola vez)
        if (puede('aprobar_registros') || user.role === 'secretaria') {
          cargarPendientes();
          cargarDepartamentos();
        }
        if (user.role === 'secretaria') {
          // cargarHistorial(); // desactivado: la sección de historial está oculta
          cargarPendientesGuia();
        }
        // Completar los datos de entrega es parte del despacho, así que
        // esta lista es de la secretaria, igual que "Pendientes de Guía".
        if (user.role === 'secretaria') {
          cargarPorCompletar();
        }
      }
    };

    // Para poder refrescar la lista después de guardar, sin recargar la página
    recargarPorCompletarRef.current = cargarPorCompletar;

    //carga inicial
    cargarDatos();

    // Actualizar periódicamente (3 minutos).
    // Intervalos cortos con varios usuarios consumen la cuota de
    // transferencia del plan gratuito de Supabase.
    const interval = setInterval(() => {
      // Solo refrescar si la pestaña está visible (ahorra cuota de Supabase)
      if (document.visibilityState !== 'visible') return;
      if (user) {
        if (puede('aprobar_registros') || user.role === 'secretaria') {
          cargarPendientes();
        }
        if (user.role === 'secretaria') {
          // cargarHistorial(); // desactivado: la sección de historial está oculta
          cargarPendientesGuia();
        }
        if (user.role === 'secretaria') {
          cargarPorCompletar();
        }
      }
    }, 180000);

    return () => clearInterval(interval);
  }, [user, filtroMeses]);

  // Funciones para modal de aprobación
  // Recibe todos los tóners de un mismo cambio (uno solo si el registro
  // es individual). Se aprueban juntos, con su tipo de tóner cada uno.
  const abrirModalAprobacion = (registros) => {
    const lista = [].concat(registros).filter(Boolean);
    if (!lista.length) return;
    setModalAprobacion({ isOpen: true, registros: lista });
    setTiposPorRegistro({});
    setPersonalSeleccionado('');
    // Si ya hay una fecha programada para este cambio, cargarla en el modal
    setFechaEntrega(lista[0].fecha_despacho || fechasDespacho[lista[0].id] || '');
    // Initialize observaciones state from registro
    setObservacionesAprobacion(lista[0]?.observaciones || '');
  };

  const cerrarModalAprobacion = () => {
    setModalAprobacion({ isOpen: false, registros: [] });
    setTiposPorRegistro({});
    setPersonalSeleccionado('');
    setFechaEntrega('');
    setObservacionesAprobacion('');
  };

  const confirmarAprobacion = async () => {
    const registros = modalAprobacion.registros || [];

    if (!registros.length) return;

    if (registros.some((r) => !tiposPorRegistro[r.id])) {
      toast.error(
        registros.length > 1
          ? 'Debe seleccionar el tipo de tóner de cada color'
          : 'Debe seleccionar un tipo de tóner'
      );
      return;
    }

    // La persona que entrega y la fecha de despacho son OPCIONALES: se
    // puede aprobar sin ellas y completarlas después, al poner la guía.

    const primero = registros[0];

    // Una fila aprobada por cada tóner, todas con el mismo grupo: así la
    // secretaria les pone UNA sola guía de despacho.
    const { data: nuevosRegistros, error } = await supabase.from('registros').insert(
      registros.map((registro) => ({
        fecha: registro.fecha,
        guia: null, // La guía se agregará después por la secretaria
        cliente_id: registro.cliente_id,
        modelo_maquina: registro.modelo_maquina,
        serie_maquina: registro.serie_maquina,
        modelo_toner_id: registro.modelo_toner_id,
        color: registro.color,
        tipo_toner_instalado_id: tiposPorRegistro[registro.id], // Tipo elegido por el admin para ese color
        ultimo_contador: registro.ultimo_contador,
        contador_actual: registro.contador_actual,
        observaciones: observacionesAprobacion, // Use edited observaciones state
        aprobado_por: user.id,
        entregado_por: personalSeleccionado || null,
        fecha_despacho: fechaEntrega || null,
        grupo_id: registro.grupo_id || null,
      }))
    ).select();

    if (error) {
      console.error('Error al aprobar registro:', error);
      toast.error(
        String(error.message || '').includes('grupo_id')
          ? 'Falta ejecutar el script 16 en la base de datos (columna grupo_id).'
          : String(error.message || '').includes('fecha_despacho')
            ? 'Falta ejecutar el script 20 en la base de datos (columna fecha_despacho).'
            : 'Error al aprobar el registro: ' + error.message
      );
      return;
    }

    // Transferir la fecha programada a los nuevos registros aprobados
    // Guardamos con múltiples claves para poder buscar desde diferentes vistas
    if (nuevosRegistros && nuevosRegistros.length > 0 && fechaEntrega) {
      // Normalizar la fecha (quitar parte de tiempo si existe)
      const fechaNormalizada = primero.fecha ? primero.fecha.split('T')[0] : primero.fecha;
      const claveSerieFecha = `${primero.serie_maquina}_${fechaNormalizada}`;
      const nuevasFechas = { [claveSerieFecha]: fechaEntrega }; // Por serie+fecha (para historial)
      nuevosRegistros.forEach((nuevo) => {
        nuevasFechas[`reg_${nuevo.id}`] = fechaEntrega; // Por ID del registro
      });

      setFechasDespacho(prev => ({ ...prev, ...nuevasFechas }));
    }

    await supabase.from('registros_historial').insert(
      registros.map((registro) => ({
        serie_maquina: registro.serie_maquina,
        resultado: 'aprobado',
        observaciones: observacionesAprobacion, // Use edited observaciones state
        creado_por: registro.creado_por,
        fecha: registro.fecha,
        cliente_id: registro.cliente_id,
        modelo_toner_id: registro.modelo_toner_id,
        color: registro.color,
        tipo_toner_id: tiposPorRegistro[registro.id], // Tipo elegido por el admin para ese color
        contador_actual: registro.contador_actual,
        ultimo_contador: registro.ultimo_contador,
        aprobado_por: user.id,
        entregado_por: personalSeleccionado || null,
        grupo_id: registro.grupo_id || null,
      }))
    );

    // Detectar si las observaciones fueron modificadas por el admin
    const observacionOriginal = primero.observaciones || '';
    const observacionEditada = observacionesAprobacion || '';
    if (observacionOriginal.trim() !== observacionEditada.trim()) {
      guardarNotificacionObservacion(primero, observacionOriginal, observacionEditada);
    }

    const ids = registros.map((r) => r.id);
    await supabase.from('registros_pendientes').delete().in('id', ids);
    setPendientes((prev) => prev.filter((p) => !ids.includes(p.id)));

    // Limpiar la fecha programada del mapa
    setFechasDespacho(prev => {
      const nuevo = { ...prev };
      ids.forEach((id) => delete nuevo[id]);
      return nuevo;
    });

    toast.success(
      registros.length > 1
        ? `${registros.length} tóners aprobados correctamente`
        : 'Registro aprobado correctamente'
    );
    cerrarModalAprobacion();
  };

  const aprobarRegistro = async (registros) => {
    // Ahora abre el modal en lugar de aprobar directamente
    abrirModalAprobacion(registros);
  };

  // Rechaza todos los tóners de un mismo cambio
  const rechazarRegistro = async (registros) => {
    const lista = [].concat(registros).filter(Boolean);
    if (!lista.length) return;

    await supabase.from('registros_historial').insert(
      lista.map((registro) => ({
        serie_maquina: registro.serie_maquina,
        resultado: 'rechazado',
        observaciones: registro.observaciones,
        creado_por: registro.creado_por,
        fecha: registro.fecha,
        cliente_id: registro.cliente_id,
        modelo_toner_id: registro.modelo_toner_id,
        color: registro.color,
        tipo_toner_id: registro.tipo_toner_instalado_id,
        ultimo_contador: registro.ultimo_contador,
        contador_actual: registro.contador_actual,
        aprobado_por: user.id,
        grupo_id: registro.grupo_id || null,
      }))
    );

    const ids = lista.map((r) => r.id);
    await supabase.from('registros_pendientes').delete().in('id', ids);
    setPendientes((prev) => prev.filter((p) => !ids.includes(p.id)));

    // Limpiar la fecha programada del mapa
    setFechasDespacho(prev => {
      const nuevo = { ...prev };
      ids.forEach((id) => delete nuevo[id]);
      return nuevo;
    });

    toast.success(lista.length > 1 ? `${lista.length} tóners rechazados` : 'Registro rechazado');
  };

  const eliminarHistorial = async (id) => {
    await supabase.from('registros_historial').delete().eq('id', id);
    setHistorial((prev) => prev.filter((r) => r.id !== id));
  };

  // Abre el modal con todos los tóners del cambio: la guía es una sola
  const abrirModalGuia = (registros) => {
    const lista = [].concat(registros).filter(Boolean);
    if (!lista.length) return;
    setGuiaModal({ isOpen: true, registros: lista });
  };

  const cerrarModalGuia = () => {
    setGuiaModal({ isOpen: false, registros: [] });
  };

  const handleGuiaActualizada = (registroIds, nuevaGuia, registros, completados) => {
    const ids = [].concat(registroIds || []).filter(Boolean);
    const lista = [].concat(registros || []).filter(Boolean);

    // Actualizar el mapa de guías inmediatamente
    if (lista.length) {
      setGuiasRegistros(prev => {
        const nuevo = { ...prev };
        lista.forEach((registro) => {
          const fechaNormalizada = registro.fecha ? registro.fecha.split('T')[0] : registro.fecha;
          nuevo[`${registro.serie_maquina}_${fechaNormalizada}_${registro.cliente_id}_${registro.color}`] = nuevaGuia;
        });
        return nuevo;
      });
    }

    // Si al poner la guía se completó la persona o la fecha de despacho,
    // se reflejan al tiro en pantalla sin recargar.
    const extras = {};
    if (completados?.entregado_por) extras.entregado_por = completados.entregado_por;
    if (completados?.fecha_despacho) extras.fecha_despacho = completados.fecha_despacho;
    // La lista de "por completar" cambia cuando se ponen estos datos
    recargarPorCompletarRef.current?.();

    setHistorial((prev) =>
      prev.map((r) =>
        ids.includes(r.id) ? { ...r, guia: nuevaGuia, ...extras } : r
      )
    );

    // Sacar los registros de la lista de "Pendientes de Guía": ya tienen guía.
    // Se compara por id y también por los datos del registro, porque el
    // modal puede entregar el identificador de distintas fuentes.
    setPendientesGuia((prev) =>
      prev.filter((r) => {
        if (ids.includes(r.id)) return false;
        const mismoRegistro = lista.some((registro) =>
          r.serie_maquina === registro.serie_maquina &&
          r.fecha === registro.fecha &&
          r.cliente_id === registro.cliente_id &&
          r.color === registro.color
        );
        return !mismoRegistro;
      })
    );
  };

  // Obtener la guía de un registro del historial
  const obtenerGuiaRegistro = (registro) => {
    const fechaNormalizada = registro.fecha ? registro.fecha.split('T')[0] : registro.fecha;
    const clave = `${registro.serie_maquina}_${fechaNormalizada}_${registro.cliente_id}_${registro.color}`;
    return guiasRegistros[clave] || null;
  };

  const verificarGuiaExiste = async (registro) => {
    try {
      const { data, error } = await supabase
        .from('registros')
        .select('guia')
        .eq('serie_maquina', registro.serie_maquina)
        .eq('fecha', registro.fecha)
        .eq('color', registro.color)
        .single();

      return !error && data && data.guia && data.guia.trim() !== '';
    } catch (error) {
      return false;
    }
  };

  const eliminarHistorialConValidacion = async (registro) => {
    //Si el registro fue rechazado, eliminar sin validar guía
    if (registro.resultado === 'rechazado') {
      await eliminarHistorial(registro.id);
      toast.success('Registro eliminado del historial');
      return;
    }

    //Solo para registros aprobados verificar si tiene guia
    const tieneGuia = await verificarGuiaExiste(registro);
    
    if (!tieneGuia) {
      toast.error('Debe agregar una guía antes de poder limpiar este registro');
      return;
    }

    await eliminarHistorial(registro.id);
    toast.success('Registro eliminado del historial');
  };

  const generarLabelsYDataset = (stats) => {
    const clavesOrdenadas = Object.keys(stats).sort((a, b) => {
      const [añoA, mesA] = a.split('-').map(Number);
      const [añoB, mesB] = b.split('-').map(Number);
      return añoA - añoB || mesA - mesB;
    });

    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const labels = clavesOrdenadas.map(clave => {
      const [año, mes] = clave.split('-').map(Number);
      return `${meses[mes]} ${año}`;
    });

    const series = new Set();
    Object.values(stats).forEach((m) => Object.keys(m).forEach((s) => series.add(s)));

    const datasets = Array.from(series).map((serie, idx) => ({
      label: serie,
      data: clavesOrdenadas.map(clave => stats[clave]?.[serie] || 0),
      backgroundColor: `hsl(${idx * 50}, 70%, 60%)`,
    }));

    return { labels, datasets };
  };

  // Diferencia memoizada para historial (secretaria)
  const historialConDiferencia = useMemo(() => {
    return historial.map(h => ({
      ...h,
      _diferencia: (h.contador_actual || 0) - (h.ultimo_contador || 0),
    }));
  }, [historial]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 pt-16 lg:pt-6 ml-0 lg:ml-72 min-h-screen animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Bienvenido, <span className="text-gradient">{user?.email || getEmailById(user.id)}</span>
            </h1>
            <p className="text-gray-600 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-sky-100 to-blue-100 text-blue-700 border border-blue-200">
                {user?.role === 'admin' ? '👑 Administrador' : '📝 Secretaria'}
              </span>
              <span className="text-gray-500 text-sm">
                {new Date().toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </p>
          </div>
          <AyudaModal titulo="¿Cómo funciona el sistema?" etiquetaBoton="¿Cómo funciona?">
            <p className="text-sm">Este sistema controla los tóner de las máquinas: cuándo se cambian, cuántas copias hacen y qué se despacha a cada cliente.</p>
            <div>
              <p className="font-semibold text-gray-900 mb-1">📋 Registros</p>
              <p className="text-sm">Cada vez que se cambia un tóner en una máquina, se anota aquí con la lectura del contador. El sistema calcula solo cuántas copias se hicieron.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">🖨️ Máquinas y Clientes</p>
              <p className="text-sm">El listado de equipos y de clientes. Cada máquina está asociada a un cliente y a un modelo de tóner.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">🚚 Envío a Clientes</p>
              <p className="text-sm">Aquí se gestiona cuántos tóner se despachan a cada cliente por mes. Tiene su propio botón "¿Cómo funciona?" con el detalle del proceso de aprobación y los pendientes.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">📊 Historial</p>
              <p className="text-sm">El registro completo de todos los envíos realizados, con su estado y número de guía.</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">💡 Consejo: usa el buscador que aparece arriba en cada listado para encontrar rápido lo que necesitas, sin ir página por página.</p>
            </div>
          </AyudaModal>
        </div>
      </div>

      {puede('aprobar_registros') && (
        <>
          {/* Card de Registros Pendientes */}
          <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 overflow-hidden mb-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl">
                  <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Registros Pendientes</h2>
                  <p className="text-sm text-gray-500">Solicitudes esperando aprobación</p>
                </div>
              </div>
              {pendientes.length > 0 && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg animate-pulse">
                  {pendientes.length} {pendientes.length === 1 ? 'pendiente' : 'pendientes'}
                </span>
              )}
            </div>

            {pendientes.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-cyan-100 mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-600">¡Todo al día!</p>
                <p className="text-sm text-gray-500 mt-1">No hay registros pendientes de aprobación</p>
              </div>
            ) : (
              <div className="space-y-3">
                {gruposPendientes.map((grupo) => (
                  <TarjetaPendiente
                    key={grupo.clave}
                    grupo={grupo}
                    modo="admin"
                    departamento={departamentoPorSerie[grupo.registros[0].serie_maquina]}
                    tipoRecomendado={tonerEsperadoPorSerie[grupo.registros[0].serie_maquina]}
                    fechaProgramada={obtenerFechaProgramadaGrupo(grupo)}
                    formatearFecha={formatearFecha}
                    onVerObservacion={(r) => setObservacionModal(r.observaciones)}
                    onAprobar={(r) => aprobarRegistro(r)}
                    onRechazar={(r) => rechazarRegistro(r)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Filtro para el gráfico de cambios mensuales */}
          <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 mb-8 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <label className="text-sm font-semibold text-gray-700">Período de análisis:</label>
                <select
                  value={filtroMeses}
                  onChange={(e) => setFiltroMeses(Number(e.target.value))}
                  className="px-4 py-2 text-sm font-medium border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white hover:border-blue-300"
                >
                  <option value={1}>Último mes</option>
                  <option value={3}>Últimos 3 meses</option>
                  <option value={4}>Últimos 4 meses</option>
                  <option value={6}>Últimos 6 meses</option>
                  <option value={12}>Último año</option>
                  <option value={24}>Últimos 2 años</option>
                </select>
              </div>
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Aplica al gráfico de cambios mensuales
              </span>
            </div>
          </div>

          {/* Gráfico de cambios mensuales */}
          <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 mb-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-sky-100 to-blue-100 rounded-xl">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Cambios por Modelo y Tipo de Tóner</h3>
                <p className="text-sm text-gray-500">Distribución mensual (Últimos {filtroMeses} {filtroMeses === 1 ? 'mes' : 'meses'})</p>
              </div>
            </div>
            <div className="h-[400px]">
              <Bar data={generarLabelsYDataset(tonerStats)} options={{ plugins: { title: { display: false }, legend: { display: true, position: 'bottom' } }, responsive: true, maintainAspectRatio: false }} />
            </div>
          </div>

          {/* Gráfico de rendimiento promedio */}
          <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 mb-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-green-100 to-cyan-100 rounded-xl">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Rendimiento Promedio por Tóner</h3>
                <p className="text-sm text-gray-500">Páginas promedio por cambio de tóner, separado por color</p>
              </div>
            </div>
            <div className="h-[400px]">
              {(() => {
                // Modelos (eje X) y colores presentes en los datos
                const modelos = Object.keys(rendimientoStats).sort();
                const coloresPresentes = [...new Set(
                  modelos.flatMap(m => Object.keys(rendimientoStats[m] || {}))
                )];

                // Orden y estilo visual de cada color (usa el color real del tóner)
                const ordenColores = ['Black', 'Cyan', 'Magenta', 'Yellow'];
                const estiloColor = {
                  Black:   { fondo: 'rgba(55, 65, 81, 0.85)',   borde: 'rgb(31, 41, 55)',    etiqueta: 'Negro' },
                  Cyan:    { fondo: 'rgba(6, 182, 212, 0.85)',  borde: 'rgb(8, 145, 178)',   etiqueta: 'Cian' },
                  Magenta: { fondo: 'rgba(217, 70, 239, 0.85)', borde: 'rgb(192, 38, 211)',  etiqueta: 'Magenta' },
                  Yellow:  { fondo: 'rgba(234, 179, 8, 0.85)',  borde: 'rgb(202, 138, 4)',   etiqueta: 'Amarillo' },
                };

                // Ordenar: primero los colores conocidos, luego cualquier otro
                const coloresOrdenados = [
                  ...ordenColores.filter(c => coloresPresentes.includes(c)),
                  ...coloresPresentes.filter(c => !ordenColores.includes(c)),
                ];

                // Un dataset por color; cada uno con su valor por modelo
                const datasets = coloresOrdenados.map(color => {
                  const estilo = estiloColor[color] || {
                    fondo: 'rgba(148, 163, 184, 0.85)', borde: 'rgb(100, 116, 139)', etiqueta: color
                  };
                  return {
                    label: estilo.etiqueta,
                    data: modelos.map(m => rendimientoStats[m]?.[color] ?? null),
                    backgroundColor: estilo.fondo,
                    borderColor: estilo.borde,
                    borderWidth: 1,
                    borderRadius: 6,
                  };
                });

                return (
                  <Bar
                    data={{ labels: modelos, datasets }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: { display: false },
                        legend: { display: true, position: 'bottom' },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => {
                              if (ctx.parsed.y === null) return null;
                              return `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString('es-CL')} páginas`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: { color: 'rgba(0, 0, 0, 0.05)' },
                          ticks: { callback: (v) => v.toLocaleString('es-CL') }
                        },
                        x: { grid: { display: false } }
                      }
                    }}
                  />
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Cambios aprobados a los que les falta la persona que entrega o la
          fecha de despacho. Se pueden completar cuando sea: antes de la guía,
          al ponerla, o mucho después. Si no falta nada, la tarjeta no aparece. */}
      {user?.role === 'secretaria' && gruposPorCompletar.length > 0 && (
        <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 mb-8 border border-amber-200">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl">
                <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Datos de entrega por completar</h2>
                <p className="text-sm text-gray-500">
                  Cambios aprobados a los que les falta la persona que entrega o la fecha de despacho
                </p>
              </div>
            </div>
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg">
              {gruposPorCompletar.length} {gruposPorCompletar.length === 1 ? 'pendiente' : 'pendientes'}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            {gruposPorCompletar.map((grupo) => {
              const primero = grupo.registros[0];
              return (
                <div key={grupo.clave} className="border border-gray-200 rounded-xl p-4 hover:border-amber-300 transition-colors duration-200">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 truncate">
                        {primero.clientes?.nombre || 'Cliente'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        <span className="font-mono">{primero.serie_maquina}</span>
                        {' · '}{formatearFecha(primero.fecha)}
                        {grupo.registros.length > 1 && ` · ${grupo.registros.length} tóners`}
                      </p>
                      <p className="text-xs text-amber-700 mt-1.5">Falta {queFalta(grupo)}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {primero.guia ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          Guía {primero.guia}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                          Sin guía
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setCompletarModal({ isOpen: true, registros: grupo.registros })}
                    className="mt-3 w-full px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg hover:shadow-lg transition-all duration-200"
                  >
                    Completar datos
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {user?.role === 'secretaria' && (
        <>
          {/* Registros aprobados que esperan número de guía.
              Se muestran todos, sin importar quién creó el registro. */}
          <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 overflow-hidden mb-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-sky-100 to-blue-100 rounded-xl">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Pendientes de Guía</h2>
                  <p className="text-sm text-gray-500">Aprobados por el administrador, esperando número de guía</p>
                </div>
              </div>
              {pendientesGuia.length > 0 && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg">
                  {gruposGuia.length} {gruposGuia.length === 1 ? 'pendiente' : 'pendientes'}
                  {pendientesGuia.length !== gruposGuia.length && ` · ${pendientesGuia.length} tóners`}
                </span>
              )}
            </div>

            {pendientesGuia.length === 0 ? (
              <div className="text-center py-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-cyan-100 mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-600">¡Todo al día!</p>
                <p className="text-sm text-gray-500 mt-1">No hay registros esperando guía de despacho</p>
              </div>
            ) : (
              <div className="space-y-3">
                {gruposGuia.map((grupo) => {
                  const primero = grupo.registros[0];
                  const varios = grupo.registros.length > 1;
                  return (
                    <div
                      key={grupo.clave}
                      className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow duration-200"
                    >
                      {/* Cliente, colores y botón */}
                      <div className="flex items-start sm:items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <h3 className="text-base font-bold text-gray-900 break-words">
                            {primero.clientes?.nombre || 'Sin cliente'}
                          </h3>
                          {grupo.registros.map((r) => (
                            <BadgeColor key={r.id} color={r.color} />
                          ))}
                          {varios && (
                            <span className="inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700">
                              {grupo.registros.length} tóners · una guía
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => abrirModalGuia(grupo.registros)}
                          className="hidden sm:inline-flex flex-shrink-0 items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Agregar Guía
                        </button>
                      </div>

                      {/* Datos del cambio (iguales para todos sus tóners) */}
                      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
                        <DatoGuia etiqueta="Fecha">
                          {new Date(primero.fecha).toLocaleDateString('es-CL')}
                        </DatoGuia>
                        <DatoGuia etiqueta="Serie">
                          <span className="font-mono">{primero.serie_maquina || '—'}</span>
                          {primero.modelo_maquina && (
                            <span className="block text-xs font-normal text-gray-500">{primero.modelo_maquina}</span>
                          )}
                        </DatoGuia>
                        <DatoGuia etiqueta="Departamento">
                          {departamentoPorSerie[primero.serie_maquina] || '—'}
                        </DatoGuia>
                        {/* Quién lleva el tóner al cliente (lo eligió el admin
                            al aprobar; si los aprobó por separado puede haber
                            más de una persona) */}
                        <DatoGuia etiqueta="Entrega">
                          {[...new Set(grupo.registros.map((r) => personalMap[r.entregado_por]).filter(Boolean))].join(', ') || '—'}
                        </DatoGuia>
                      </dl>

                      {/* Un bloque por cada tóner que se cambió */}
                      <div className="mt-3 space-y-2">
                        {grupo.registros.map((r) => {
                          const diferencia = (r.contador_actual || 0) - (r.ultimo_contador || 0);
                          return (
                            <div key={r.id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <BadgeColor color={r.color} />
                                <span className="text-sm font-semibold text-gray-900">
                                  {r.toners?.modelo || '—'}
                                  {r.tipos_toner_instalado?.nombre && (
                                    <span className="font-normal text-gray-500"> ({r.tipos_toner_instalado.nombre})</span>
                                  )}
                                </span>
                              </div>
                              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                                <DatoGuia etiqueta="Contador anterior">
                                  {formatearNumeroCL(r.ultimo_contador)}
                                </DatoGuia>
                                <DatoGuia etiqueta="Contador actual">
                                  {formatearNumeroCL(r.contador_actual)}
                                </DatoGuia>
                                <DatoGuia etiqueta="Diferencia" destacado>
                                  {formatearNumeroCL(diferencia)} <span className="font-normal">copias</span>
                                </DatoGuia>
                              </dl>
                            </div>
                          );
                        })}
                      </div>

                      {/* En celular el botón va al final, a lo ancho */}
                      <button
                        onClick={() => abrirModalGuia(grupo.registros)}
                        className="sm:hidden mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Agregar Guía
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Card de Registros Pendientes para Secretaria */}
          <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 overflow-hidden mb-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl">
                  <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Registros Pendientes de Aprobación</h2>
                  <p className="text-sm text-gray-500">Esperando revisión del administrador</p>
                </div>
              </div>
              {pendientes.length > 0 && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg">
                  {pendientes.length} {pendientes.length === 1 ? 'pendiente' : 'pendientes'}
                </span>
              )}
            </div>

            {pendientes.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-cyan-100 mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-600">¡Todo al día!</p>
                <p className="text-sm text-gray-500 mt-1">No hay registros pendientes de aprobación</p>
              </div>
            ) : (
              <div className="space-y-3">
                {gruposPendientes.map((grupo) => (
                  <TarjetaPendiente
                    key={grupo.clave}
                    grupo={grupo}
                    modo="secretaria"
                    departamento={departamentoPorSerie[grupo.registros[0].serie_maquina]}
                    tipoRecomendado={tonerEsperadoPorSerie[grupo.registros[0].serie_maquina]}
                    fechaProgramada={obtenerFechaProgramadaGrupo(grupo)}
                    formatearFecha={formatearFecha}
                    onVerObservacion={(r) => setObservacionModal(r.observaciones)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ─────────────────────────────────────────────────────────────
              HISTORIAL DE REGISTROS — OCULTO
              Se ocultó porque esta información ya está disponible en la
              página "Registros", con buscador y filtros. Mantenerla aquí
              alargaba el Dashboard sin aportar nada nuevo.
              Para volver a mostrarla: quitar esta línea de apertura y la
              de cierre que está al final del bloque.
              ───────────────────────────────────────────────────────────── */}
          {false && (
          <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Historial de Registros</h2>
                  <p className="text-sm text-gray-500">Registros aprobados y rechazados (últimos 100)</p>
                </div>
              </div>
              {notificacionesObservaciones.length > 0 && (
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg animate-pulse">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {notificacionesObservaciones.length} {notificacionesObservaciones.length === 1 ? 'observación editada' : 'observaciones editadas'}
                  </span>
                  <button
                    onClick={marcarTodasNotificacionesLeidas}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Marcar todas como leídas"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Marcar leídas
                  </button>
                </div>
              )}
            </div>

            {historialConDiferencia.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-600">Sin registros</p>
                <p className="text-sm text-gray-500 mt-1">No hay registros en el historial aún</p>
              </div>
            ) : (
              <TablaScroll>
                <table className="min-w-[1300px] w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Cliente</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Serie</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Modelo</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Color</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tipo</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Estado</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Guía</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Será Despachado</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Observaciones</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contador</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Diferencia</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rendimiento</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Autorizado</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Entrega</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {historialConDiferencia.map((r) => {
                      const rendToner = rendimientoSegunColor(r.toners, r.color);
                      const rendimiento = rendToner ? formatearNumeroCL(rendToner) : '—';
                      return (
                        <tr key={r.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-all duration-200">
                          <td className="px-3 py-3 whitespace-nowrap text-gray-700">{formatearFecha(r.fecha)}</td>
                          <td className="px-3 py-3 text-gray-700">{r.clientes?.nombre}</td>
                          <td className="px-3 py-3 font-mono text-gray-900">{r.serie_maquina}</td>
                          <td className="px-3 py-3 font-medium text-gray-900">{r.toners?.modelo}</td>
                          <td className="px-3 py-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                              {r.color}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-gray-700">{r.tipos_toner?.nombre}</td>
                          <td className="px-3 py-3 text-center">
                            {r.resultado === 'aprobado' ? (
                              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-green-100 to-cyan-100 text-green-700 border border-green-200">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Aprobado
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-red-100 to-orange-100 text-red-700 border border-red-200">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Rechazado
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {r.resultado === 'aprobado' ? (
                              obtenerGuiaRegistro(r) ? (
                                <span
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-cyan-500 rounded-lg cursor-default"
                                  title={`Guía: ${obtenerGuiaRegistro(r)}`}
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  {obtenerGuiaRegistro(r)}
                                </span>
                              ) : (
                                // Sin guía todavía: solo se informa. La acción de
                                // agregarla vive en la sección "Pendientes de Guía"
                                // (arriba), para no duplicar el mismo botón.
                                <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Sin guía
                                </span>
                              )
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-xs">
                            {obtenerFechaProgramada(r) ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatearFechaSinZonaHoraria(obtenerFechaProgramada(r))}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {r.observaciones ? (
                              <div className="relative inline-block">
                                <button
                                  onClick={() => verObservacionConNotificacion(r)}
                                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors duration-200 ${
                                    tieneNotificacionObservacion(r)
                                      ? 'text-white bg-gradient-to-r from-orange-500 to-red-500 border border-orange-400 animate-pulse hover:from-orange-600 hover:to-red-600'
                                      : 'text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100'
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  {tieneNotificacionObservacion(r) ? 'Editado' : 'Ver'}
                                </button>
                                {tieneNotificacionObservacion(r) && (
                                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-gray-700">
                            <span className="text-gray-500">{formatearNumeroCL(r.ultimo_contador)}</span>
                            <span className="mx-1">→</span>
                            <span className="font-semibold">{formatearNumeroCL(r.contador_actual)}</span>
                          </td>
                          <td className="px-3 py-3 font-semibold text-indigo-600">
                            {formatearNumeroCL((r.contador_actual || 0) - (r.ultimo_contador || 0))}
                          </td>
                          <td className="px-3 py-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              {rendimiento}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-gray-700 text-xs">{getEmailById(r.aprobado_por)}</td>
                          <td className="px-3 py-3 text-gray-700">
                            {r.resultado === 'aprobado' ? (
                              <span className="text-sm font-medium text-blue-700">
                                {personalMap[r.entregado_por] || '—'}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <button
                              onClick={() => eliminarHistorialConValidacion(r)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-red-500 to-orange-500 rounded-lg hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Limpiar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TablaScroll>
            )}
          </div>
          )}
        </>
      )}

      {observacionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden border border-gray-200 transform transition-all duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-white">Observación del Registro</h2>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <p className="text-gray-800 whitespace-pre-line break-words leading-relaxed">
                  {observacionModal}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setObservacionModal(null)}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Aprobación con Selección de Tipo de Tóner */}
      {modalAprobacion.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-auto animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl border border-gray-200 transform transition-all duration-300 max-h-[90vh] overflow-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-cyan-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-white">
                  {registrosAprobacion.length > 1
                    ? `Aprobar cambio de ${registrosAprobacion.length} tóners`
                    : 'Aprobar Registro de Tóner'}
                </h2>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* Información del cambio (igual para todos sus tóners) */}
              {primerAprobacion && (
                <div className="mb-6 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Información del Registro</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Serie</p>
                      <p className="font-mono font-semibold text-gray-900">{primerAprobacion.serie_maquina}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Cliente</p>
                      <p className="font-semibold text-gray-900">{primerAprobacion.clientes?.nombre}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Departamento</p>
                      <p className="font-semibold text-gray-900">{departamentoPorSerie[primerAprobacion.serie_maquina] || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Fecha</p>
                      <p className="font-semibold text-gray-900">{formatearFecha(primerAprobacion.fecha)}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Tipo Recomendado</p>
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                        {tonerEsperadoPorSerie[primerAprobacion.serie_maquina] || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

            {/* Tipo de tóner: uno por cada color del cambio */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tipo de tóner a despachar *
                {registrosAprobacion.length > 1 && (
                  <span className="ml-1 font-normal text-gray-500">(elige el de cada color)</span>
                )}
              </label>
              <div className="space-y-3">
                {registrosAprobacion.map((r) => (
                  <div
                    key={r.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50"
                  >
                    <div className="flex items-center gap-2 sm:w-56 flex-shrink-0">
                      <BadgeColor color={r.color} />
                      <span className="text-sm font-semibold text-gray-900">{r.toners?.modelo || '—'}</span>
                    </div>
                    <select
                      value={tiposPorRegistro[r.id] || ''}
                      onChange={(e) =>
                        setTiposPorRegistro((prev) => ({
                          ...prev,
                          [r.id]: e.target.value ? Number(e.target.value) : '',
                        }))
                      }
                      className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white hover:border-blue-300"
                      required
                    >
                      <option value="">-- Seleccionar tipo de tóner --</option>
                      {tiposToner.map(tipo => (
                        <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Select de personal */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Persona encargada de la entrega
                <span className="font-normal text-gray-500"> (opcional)</span>
              </label>
              <select
                value={personalSeleccionado}
                onChange={(e) => setPersonalSeleccionado(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white hover:border-blue-300"
              >
                <option value="">-- Seleccionar persona --</option>
                {personalEmpresa.map(persona => (
                  <option key={persona.id} value={persona.id}>{persona.nombre}</option>
                ))}
              </select>
            </div>

            {/* Campo de fecha - solo visual */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Será Despachado
                <span className="font-normal text-gray-500"> (opcional)</span>
              </label>
              <input
                type="date"
                value={fechaEntrega}
                onChange={(e) => setFechaEntrega(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white hover:border-blue-300"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Si todavía no está definido, puedes dejarlo en blanco y completarlo al poner la guía.
              </p>
            </div>

            {/* Editable textarea for observaciones */}
            <div className="mb-6">
              <label htmlFor="observaciones" className="block text-sm font-semibold text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                id="observaciones"
                value={observacionesAprobacion}
                onChange={(e) => setObservacionesAprobacion(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white hover:border-blue-300 resize-y"
                rows={4}
                placeholder="Ingrese observaciones adicionales (opcional)"
              />
            </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={cerrarModalAprobacion}
                className="px-5 py-2.5 text-gray-700 bg-white border-2 border-gray-300 font-medium rounded-xl hover:bg-gray-50 transition-all duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarAprobacion}
                disabled={faltaTipoEnAlgunColor}
                className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-cyan-500 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-none inline-flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {registrosAprobacion.length > 1 ? `Aprobar los ${registrosAprobacion.length} tóners` : 'Aprobar Registro'}
              </button>
            </div>
          </div>
        </div>
      )}

      <GuiaModal
        isOpen={guiaModal.isOpen}
        onClose={cerrarModalGuia}
        registros={guiaModal.registros}
        entrega={[...new Set((guiaModal.registros || []).map((r) => personalMap[r.entregado_por]).filter(Boolean))].join(', ')}
        personalEmpresa={personalEmpresa}
        onGuiaActualizada={handleGuiaActualizada}
      />

      <CompletarEntregaModal
        isOpen={completarModal.isOpen}
        registros={completarModal.registros}
        personalEmpresa={personalEmpresa}
        onClose={() => setCompletarModal({ isOpen: false, registros: [] })}
        onListo={() => recargarPorCompletarRef.current?.()}
      />
    </div>
  );
}

// ---------------------------------------------------------------------
//  Auxiliares de la sección "Pendientes de Guía"
// ---------------------------------------------------------------------

// Etiqueta de color con el color real del tóner
const COLORES_BADGE = {
  black: 'bg-gray-800 text-white',
  cyan: 'bg-cyan-100 text-cyan-800',
  magenta: 'bg-pink-100 text-pink-800',
  yellow: 'bg-yellow-100 text-yellow-800',
};

function BadgeColor({ color }) {
  if (!color) return null;
  const clases = COLORES_BADGE[String(color).trim().toLowerCase()] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${clases}`}>
      {color}
    </span>
  );
}

// Un dato con su título arriba (ej: "Contador actual" / 1.240.000)
function DatoGuia({ etiqueta, destacado = false, children }) {
  return (
    <div className={destacado ? 'rounded-lg bg-indigo-50 px-2 py-1 -mx-2 -my-1' : ''}>
      <dt className={`text-[11px] font-semibold uppercase tracking-wide ${destacado ? 'text-indigo-600' : 'text-gray-500'}`}>
        {etiqueta}
      </dt>
      <dd className={`mt-0.5 text-sm font-semibold tabular-nums ${destacado ? 'text-indigo-700' : 'text-gray-900'}`}>
        {children}
      </dd>
    </div>
  );
}

// Junta los tóners que se cambiaron en la misma visita (mismo grupo_id).
// Un registro sin grupo —todos los anteriores a esta versión— queda solo,
// tal como se mostraba antes. Se respeta el orden en que vienen.
function agruparRegistros(lista) {
  const grupos = [];
  const porClave = new Map();

  (lista || []).forEach((registro) => {
    // Los registros nuevos traen grupo_id. Los anteriores no, así que se
    // juntan por máquina, fecha y lectura del contador: si coinciden, es el
    // mismo cambio (un tóner de cada color en la misma visita).
    const mismoCambio =
      registro.serie_maquina && registro.fecha && registro.contador_actual != null
        ? `${registro.serie_maquina}|${registro.fecha}|${registro.cliente_id || ''}|${registro.contador_actual}`
        : null;
    const clave = registro.grupo_id
      ? `grupo-${registro.grupo_id}`
      : (mismoCambio || `solo-${registro.id}`);
    if (!porClave.has(clave)) {
      const grupo = { clave, registros: [] };
      porClave.set(clave, grupo);
      grupos.push(grupo);
    }
    porClave.get(clave).registros.push(registro);
  });

  return grupos;
}

// ---------------------------------------------------------------------
//  Tarjeta de un cambio pendiente de aprobación
// ---------------------------------------------------------------------
//  Muestra juntos los tóners de un mismo cambio, con los datos que se
//  repiten arriba (fecha, máquina, departamento) y un bloque por tóner.
//  modo = "admin"      → cada tóner con sus botones Aprobar / Rechazar
//  modo = "secretaria" → solo lectura, con el estado del cambio
function TarjetaPendiente({
  grupo,
  modo = 'admin',
  departamento,
  tipoRecomendado,
  fechaProgramada,
  formatearFecha,
  onVerObservacion,
  onAprobar,
  onRechazar,
}) {
  const registros = grupo.registros;
  const primero = registros[0];
  const varios = registros.length > 1;

  return (
    <div className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow duration-200">
      {/* Cliente, colores y estado */}
      <div className="flex items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <h3 className="text-base font-bold text-gray-900 break-words">
            {primero.clientes?.nombre || 'Sin cliente'}
          </h3>
          {registros.map((r) => (
            <BadgeColor key={r.id} color={r.color} />
          ))}
          {varios && (
            <span className="inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700">
              {registros.length} tóners en el mismo cambio
            </span>
          )}
        </div>
        {modo === 'secretaria' && (
          <span className="hidden sm:inline-flex flex-shrink-0 items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 border border-amber-200">
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Esperando aprobación
          </span>
        )}
      </div>

      {/* Datos del cambio (iguales para todos sus tóners) */}
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
        <DatoGuia etiqueta="Fecha">{formatearFecha(primero.fecha)}</DatoGuia>
        <DatoGuia etiqueta="Serie">
          <span className="font-mono">{primero.serie_maquina || '—'}</span>
          {primero.modelo_maquina && (
            <span className="block text-xs font-normal text-gray-500">{primero.modelo_maquina}</span>
          )}
        </DatoGuia>
        <DatoGuia etiqueta="Departamento">{departamento || '—'}</DatoGuia>
        <DatoGuia etiqueta="Tipo recomendado">{tipoRecomendado || '—'}</DatoGuia>
      </dl>

      {/* Fecha de despacho, si ya quedó programada */}
      {fechaProgramada && (
        <p className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Será despachado el {fechaProgramada}
        </p>
      )}

      {/* Un bloque por cada tóner del cambio */}
      <div className="mt-3 space-y-2">
        {registros.map((r) => {
          const diferencia = (r.contador_actual || 0) - (r.ultimo_contador || 0);
          const rendimiento = rendimientoSegunColor(r.toners, r.color);
          return (
            <div key={r.id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <BadgeColor color={r.color} />
                <span className="text-sm font-semibold text-gray-900">{r.toners?.modelo || '—'}</span>
              </div>

              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
                <DatoGuia etiqueta="Contador anterior">{formatearNumeroCL(r.ultimo_contador)}</DatoGuia>
                <DatoGuia etiqueta="Contador actual">{formatearNumeroCL(r.contador_actual)}</DatoGuia>
                <DatoGuia etiqueta="Diferencia" destacado>
                  {formatearNumeroCL(diferencia)} <span className="font-normal">copias</span>
                </DatoGuia>
                <DatoGuia etiqueta="Rendimiento del tóner">
                  {rendimiento ? `${formatearNumeroCL(rendimiento)} copias` : '—'}
                </DatoGuia>
              </dl>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                {r.observaciones ? (
                  <button
                    onClick={() => onVerObservacion(r)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Ver observación
                  </button>
                ) : (
                  <span className="text-xs text-gray-400">Sin observaciones</span>
                )}

                {modo === 'admin' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onAprobar(r)}
                      className="inline-flex items-center gap-1 whitespace-nowrap px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-cyan-500 rounded-lg hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                      title={varios ? `Aprobar solo el tóner ${r.color}` : 'Aprobar'}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Aprobar
                    </button>
                    <button
                      onClick={() => onRechazar(r)}
                      className="inline-flex items-center gap-1 whitespace-nowrap px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-red-500 to-orange-500 rounded-lg hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                      title={varios ? `Rechazar solo el tóner ${r.color}` : 'Rechazar'}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Rechazar
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
